// UserRequestDetail.jsx
import React from "react";
import { getUserEmail } from "../../services/apiClient";
import { useNavigate, useParams } from "react-router-dom";
import RecurringSetting from "../../components/common/recurring-setting";
import RecurringPreview from "../../components/common/recurring-preview";
import UserInfoSection from "../user/sections/user-info-section";
import DocumentDeliverySection from "../user/sections/document-delivery-section";
import BankingSection from "../user/sections/banking-section";
import TransactionSection from "../user/sections/transaction-section";
import FormDatePicker from "../../components/common/form-date-picker";
import { useUserRequestForm } from "../user/user-request-form";
import { extractTimeHHMM, isOtherPickupDetail, isOtherBuildingDetail, isOtherProviderBranch } from "../user/user-request-form.utils";
import { isDocumentDelivery, isCompletionTimeNotPast } from "./constants";
import RequestService from "../../services/requestService";
import UserService from "../../services/userService";
import Swal from "sweetalert2";
import { useState, useEffect, useRef } from "react";
import useHolidays from "../../hooks/use-holidays";
import { showLoading, hideLoading, showConfirm, showError, showCancelRequestSuccess, showSaveSuccess } from "../../components/utils/swal-helpers";
import { printDeliverySlip } from '../../components/utils/print-delivery-slip';
import SearchableSelect from "../../components/common/searchable-select";
import { WF } from "../../components/utils/wf-constants";

const DAY_INDEX_TO_LABEL = {
  0: "วันจันทร์", 1: "วันอังคาร", 2: "วันพุธ",
  3: "วันพฤหัสบดี", 4: "วันศุกร์", 5: "วันเสาร์", 6: "วันอาทิตย์",
};

// ── workflow status ที่เป็น "Admin action" จริงๆ (ไม่รวม step ที่ messenger ทำ เช่น DOING/COMPLETE/INCOMPLETE) ──
const ADMIN_ACTION_WF = [3, 4, 5, 9, 10, 11]; // RETURN, ADMIN_CANCEL, ASSIGN, REASSIGN, CLOSE_OK, CLOSE_COND

// ── id_service_type ของ "ทำธุรกรรม/ชำระบริการ" ตาม enums.ServiceType (ServiceTypeTransactionAndPayment) ──
const SERVICE_TYPE_TRANSACTION = 3;

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  return `${day}/${month}/${d.getFullYear()}`;
};

// ── วันที่วันนี้ในรูปแบบ "YYYY-MM-DD" (local time) ใช้เทียบกับ service_date ──
const getTodayStr = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
};

// ── คำนวณเวลาปัจจุบัน (HH:mm) ใช้เป็น min ของ time input เมื่อ service_date คือ "วันนี้" ──
// ── ถ้า service_date เป็นวันอื่น (อนาคต) return undefined เพื่อไม่ jำกัดเวลาเลย ──
const getMinCompletionTime = (serviceDateStr) => {
  if (serviceDateStr !== getTodayStr()) return undefined;
  const now = new Date();
  const hh = String(now.getHours()).padStart(2, "0");
  const mm = String(now.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
};

// ── focus ไปที่ field แรกที่ error หลัง validate ไม่ผ่าน ──
// ── ลองหาด้วย id ก่อน (SearchableSelect, input ทั่วไป) ถ้าไม่เจอลองหาด้วย name (RadioGroup) ──
const focusFirstErrorField = (errors) => {
  const keys = Object.keys(errors);
  if (!keys.length) return;
  // ── delay 1 tick ให้ React render error/touched state ก่อน ค่อยหา element ──
  setTimeout(() => {
    for (const key of keys) {
      const el = document.getElementById(key) || document.getElementsByName(key)[0];
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        if (typeof el.focus === "function") el.focus();
        return;
      }
    }
  }, 0);
};

const RadioGroup = ({ name, options, value, onChange, disabled, touched, error }) => (
  <div className="radio-group">
    {options.map((opt) => (
      <label className="radio-option" key={opt.value}>
        <input
          type="radio"
          name={name}
          value={opt.value}
          checked={String(value) === String(opt.value)}
          onChange={() => onChange(opt.value)}
          disabled={disabled}
        />
        {opt.label}
      </label>
    ))}
    {touched && error && (
      <div className="invalid-feedback" style={{ display: "block" }}>{error}</div>
    )}
  </div>
);

const CloneSection = ({ isCloneData, setIsCloneData, cloneRefNo, setCloneRefNo, loadingClone }) => (
  <>
    <label className="clone-toggle">
      <input
        type="checkbox"
        checked={isCloneData}
        onChange={(e) => setIsCloneData(e.target.checked)}
      />
      Duplicate from existing request
    </label>
    {isCloneData && (
      <div className="clone-row">
        <div className="form-group input-clone">
          <input
            className="form-control"
            value={cloneRefNo}
            onChange={(e) => setCloneRefNo(e.target.value)}
            placeholder="Enter Reference No."
          />
        </div>
        {loadingClone && <span className="clone-searching">กำลังค้นหา...</span>}
      </div>
    )}
  </>
);

const AccordionCard = ({ title, refNo, isOpen, onToggle, onRemove, showRemove, disabled, children }) => (
  <div className={`accordion-card${isOpen ? " accordion-card--open" : ""}`}>
    <div className="accordion-card__header" onClick={onToggle}>
      <div className="accordion-card__header-left">
        <div className="accordion-card__title-group">
          <div className="accordion-card__title-row">
            <i className={`accordion-card__chevron ${isOpen ? "ri-arrow-up-s-fill" : "ri-arrow-down-s-fill"}`} />
            <div className="accordion-card__title">{title}</div>
          </div>
          {refNo && <div className="accordion-card__refno">Ref No. {refNo}</div>}
        </div>
      </div>
      <div className="accordion-card__header-right">
        {showRemove && (
          <button
            type="button"
            className="btn btn-danger-outline btn-sm accordion-card__remove-btn"
            onClick={(e) => { e.stopPropagation(); onRemove(); }}
            disabled={disabled}
          >
            <i className="mdi mdi-trash-can-outline" />
          </button>
        )}
      </div>
    </div>
    {isOpen && <div className="accordion-card__body">{children}</div>}
  </div>
);

const MessengerSection = ({ messenger }) => {
  if (!messenger) return null;
  return (
    <div className="messenger-section">
      <div className="sub-section-label" style={{ marginTop: 16 }}>
        Messenger (พนักงานที่ส่งเอกสาร)
      </div>
      <div className="form-row">
        <div className="form-group">
          <label className="form-label">ชื่อ-สกุล (Name-Surname)</label>
          <input className="form-control" value={`${messenger.name} ${messenger.surname}`} disabled />
        </div>
        <div className="form-group">
          <label className="form-label">เบอร์ติดต่อ (Tel.)</label>
          <input className="form-control" value={messenger.tel} disabled />
        </div>
      </div>
    </div>
  );
};

const AdminSection = ({ admin }) => {
  if (!admin) return null;
  return (
    <div className="messenger-section">
      <div className="sub-section-label" style={{ marginTop: 16 }}>
        Admin (ผู้ดูแลงาน)
      </div>
      <div className="form-row">
        <div className="form-group">
          <label className="form-label">ชื่อ-สกุล (Name-Surname)</label>
          <input className="form-control" value={`${admin.name} ${admin.surname}`} disabled />
        </div>
        <div className="form-group">
          <label className="form-label">เบอร์ติดต่อ (Tel.)</label>
          <input className="form-control" value={admin.tel || '-'} disabled />
        </div>
      </div>
    </div>
  );
};

const DELIVERY_TYPE_OPTIONS = [
  { value: 1, label: "รับเท่านั้น" },
  { value: 2, label: "ส่งเท่านั้น" },
  { value: 3, label: "ส่งและรับกลับ" },
];

const DOCUMENT_SECRET_OPTIONS = [
  { value: 1, label: "ปกติ (Normal)" },
  { value: 2, label: "ลับเฉพาะ (Confidential)" },
];

const COMPLETION_TIME_OPTIONS = [
  { value: 1, label: "เสร็จวันนี้เท่านั้น (Complete today)" },
  { value: 2, label: "ระบุเวลา (Set a specific time)" },
];

// ── REMOVED: PAYMENT_TYPE_OPTIONS hardcode array ──
// ── เดิม hardcode "ค่าน้ำ/ค่าไฟ/ค่าโทรศัพท์" ด้วย value คงที่ "3","2","4" ซึ่งไม่ตรงกับ id จริงใน DB (m_sub_service_type) ──
// ── ตอนนี้ดึงจาก subServiceTypes (จาก useUserRequestForm hook, backend: GET /sub-service-types หรือ /sub-service-types/search) แทน ──

const DELIVERY_SECTION_OPTIONS = [
  { value: "1", label: "Morning/เช้า" },
  { value: "2", label: "Afternoon/บ่าย" },
  { value: "3", label: "Urgent ด่วน (ติดต่อฝ่ายธุรการ)" },
];

const OTHER_PICKUP_VALUE = "other";

const UserRequestDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const {
    dataUser, formIsDisabled: hookFormIsDisabled,
    pickupLocations, loadingLocations,
    serviceTypes,
    providers, providerBranches, loadingProviders, loadingBranches,
    fetchProviders, fetchProviderBranches,
    destProviders, destProviderBranches, loadingDestProviders, loadingDestBranches,
    fetchDestProviders, fetchDestProviderBranches,
    setDestProviders, setDestProviderBranches,
    subServiceTypes, loadingSubServiceTypes,
    fetchSubServiceTypes,
    buildings, provinces, districts, loadingDistricts,
    fetchDistricts,
    destDistricts,
    loadingDestDistricts,
    fetchDestDistricts,
    isCloneData, setIsCloneData, cloneRefNo, setCloneRefNo,
    showRecurring, setShowRecurring, recurringConfig,
    showRecurringPreview, setShowRecurringPreview,
    recurringPreviewDates,
    handleReccurringSave, handleRecurringBack, handleRecurringDelete,
    validation: F, fProps, showErr,
    handleServiceTypeChange,
    handleProvinceChange,
    destinations, addDestination, removeDestination, updateDestination,
    applyCloneDestinations,
    MAX_EXTRA_DESTINATIONS,
    setDataDetail,
    createdRefNo,
    buildMainPayload,
    refNo,
    isViewMode,
    transactionProviders, loadingTransactionProviders,
    transactionBranches, loadingTransactionBranches,
    fetchTransactionProviders,
    fetchTransactionBranches,
    validateDestinations, showDestErr, setDestinationErrorsField,
  } = useUserRequestForm();

  const EDITABLE_WF_STATUSES = [WF.CREATE, WF.RETURN];
  const formIsDisabled = hookFormIsDisabled ||
    (isViewMode && !EDITABLE_WF_STATUSES.includes(F.values.id_workflow_status));

  // ── FIX: ล็อค field ข้อมูลผู้ติดต่อ/ปลายทาง (ชื่อ-สกุล, เบอร์โทร, บริษัท, แผนก, ชั้น) เมื่อ status ไม่ใช่ NEW ──
  // ── ต่างจาก formIsDisabled ตรงที่สถานะ RETURN ยังแก้ field อื่นได้ (เช่นอาคาร/สาขา) แต่กลุ่มนี้ต้องล็อคไว้เสมอ ──
  // ── ยกเว้นตอนสร้างคำขอใหม่ (isViewMode = false, ยังไม่มี id_workflow_status จริง) ต้องแก้ไขได้ตามปกติ ──
  const lockContactInfo = formIsDisabled;

  const paymentTypeOptions = (subServiceTypes || [])
    .filter((item) => Number(item.id_service_type) === SERVICE_TYPE_TRANSACTION)
    .map((item) => ({ value: String(item.id), label: item.name }));

  const [returnNote, setReturnNote] = useState('');
  const [adminInfo, setAdminInfo] = useState(null);
  const [senderInfo, setSenderInfo] = useState(null);

  useEffect(() => {
    const wf = F.values.id_workflow_status;
    if (F.values.id && (wf === WF.RETURN || wf === WF.ADMIN_CANCEL)) {
      loadWorkflowNote(F.values.id, wf);
    } else {
      setReturnNote('');
    }
  }, [F.values.id, F.values.id_workflow_status]);

  const loadWorkflowNote = async (requestId, wfStatus) => {
    try {
      const resp = await RequestService.SearchRequestWorkflows({
        id_request: requestId,
        id_workflow_status: wfStatus,
      });
      if (resp.data.is_success && resp.data.result?.length > 0) {
        const latest = resp.data.result[resp.data.result.length - 1];
        setReturnNote(latest.note || '');
      }
    } catch (err) {
      console.error('loadWorkflowNote error:', err);
    }
  };

  // ── โหลด admin ที่ action ล่าสุด ──
  useEffect(() => {
    if (!F.values.id || F.values.id === 0) return;
    loadAdminInfo(F.values.id);
  }, [F.values.id]);

  const loadAdminInfo = async (requestId) => {
    try {
      const wfResp = await RequestService.SearchRequestWorkflows({ id_request: requestId });
      if (!wfResp.data.is_success || !wfResp.data.result?.length) return;

      const adminSteps = wfResp.data.result.filter(
        (w) => ADMIN_ACTION_WF.includes(w.id_workflow_status)
      );
      // console.log('all workflows:', wfResp.data.result)
      if (!adminSteps.length) return;

      const latest = adminSteps[adminSteps.length - 1];
      const email = latest.do_by;
      if (!email) return;
      const userResp = await UserService.SearchUser({ email });
      if (!userResp.data.is_success || !userResp.data.result?.length) return;
      setAdminInfo(userResp.data.result[0]);
    } catch (err) {
      console.error('loadAdminInfo error:', err);
    }
  };

  // ── Reporter role (id_user_group = 4) ต้องเห็นข้อมูล sender ตัวจริงของ request นี้ ──
  // ── ไม่ใช่ข้อมูลของตัวเองที่ login อยู่ (dataUser) เพราะ Reporter อาจเข้ามาดู request ของคนอื่น ──
  useEffect(() => {
    if (!F.values.id || F.values.id === 0) return;
    if (dataUser?.id_user_group !== 4) return; // ── เฉพาะ Reporter role เท่านั้นที่ต้อง fetch ──
    loadSenderInfo(F.values.id);
  }, [F.values.id, dataUser]);

  const loadSenderInfo = async (requestId) => {
    try {
      const res = await RequestService.GetRequestById(requestId);
      const source = res.data.result;
      if (source?.sender_user) {
        setSenderInfo({
          ...source.sender_user,
          tel: source.sender_user.tel || source.sender_tel || "",
        });
      }
    } catch (err) {
      console.error('loadSenderInfo error:', err);
    }
  };

  const handlePrint = async () => {
    try {
      showLoading();
      await printDeliverySlip(F.values, destinations, refNo);
    } finally {
      hideLoading();
    }
  };

  const handleCancelRequest = async () => {
    const result = await showConfirm({
      title: "แน่ใจนะ! ว่าต้องการลบคำขอนี้",
      detail: "กด Confirm เพื่อดำเนินการต่อ",
    });
    if (!result.isConfirmed) return;
    try {
      showLoading();
      // ── ยกเลิกเฉพาะ request นี้ตัวเดียว (user cancel, wf 1→2) ──
      // ── ไม่ส่ง request_recurring จึงไม่ regenerate series และ sibling ที่เหลือไม่กระทบ ──
      await RequestService.ChangeWorkflowStatus(F.values.id, {
        id_workflow_status: 2, // WorkflowStatusUserCancelRequest (จาก status 1 เท่านั้น)
        note: "",
      });
      hideLoading();
      await showCancelRequestSuccess();
      navigate("/user/requests", { replace: true });
    } catch (err) {
      hideLoading();
      console.error("CancelRequest error:", err?.response?.data);
      showError();
    }
  };

  const handleSaveRequest = async (e) => {
      e.preventDefault();

      // ── validate ก่อนแสดง confirm dialog: ต้อง touch ทุก field เพื่อให้ error message โผล่ ──
      const errors = await F.validateForm();
      if (Object.keys(errors).length > 0) {
        const touchedAll = Object.keys(F.values).reduce((acc, key) => {
          acc[key] = true;
          return acc;
        }, {});
        F.setTouched(touchedAll);
        focusFirstErrorField(errors);
        return;
      }

      // ── validate destinations (จุดหมายที่ 2, 3) ──
      if (!validateDestinations()) {
        // ── เปิด accordion จุดหมายทุกอันไว้ก่อน เผื่อ error ซ่อนอยู่ในจุดที่ยังปิดอยู่ ──
        setOpenCards((prev) => {
          const next = { ...prev };
          destinations.forEach((d) => { next[d._id] = true; });
          return next;
        });
        return;
      }

      const result = await showConfirm({
        title: "ยืนยันการเปลี่ยนแปลงข้อมูล",
        detail: "กด Confirm เพื่อดำเนินการต่อ",
      });
      if (!result.isConfirmed) return;
      try {
        showLoading();
        const payload = buildMainPayload();
        const res = await RequestService.SearchRequest({ id: F.values.id });
        const source = res.data.result?.find(r => r.id === F.values.id);
        const existingRecurring = source?.request_recurring ?? null;

        const finalPayload = { ...payload };
        if (payload.request_recurring) {
          finalPayload.request_recurring = {
            ...payload.request_recurring,
            ...(existingRecurring && {
              id: existingRecurring.id,
              id_request: existingRecurring.id_request,
            }),
          };
        } else {
          finalPayload.request_recurring = null;
        }

        await RequestService.PatchRequest(F.values.id, finalPayload);
        hideLoading();
        await showSaveSuccess();
        navigate("/user/requests", { replace: true });
      } catch (err) {
        hideLoading();
        console.error("SaveRequest error:", err?.response?.data);
        showError();
      }
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();

    // ── validate main form (จุดหมายที่ 1) และ destinations (จุดหมายที่ 2, 3) พร้อมกันเสมอ ──
    // ── เดิม validateDestinations() ถูกเรียกใน onSubmit ของ Formik ซึ่งจะรันก็ต่อเมื่อ
    //     main form ผ่าน validation ก่อนเท่านั้น ทำให้จุดหมายอื่นไม่ถูกเช็คถ้า main form ยังไม่ผ่าน ──
    const mainErrors = await F.validateForm();
    const destinationsValid = validateDestinations();

    if (Object.keys(mainErrors).length > 0) {
      const touchedAll = Object.keys(F.values).reduce((acc, key) => {
        acc[key] = true;
        return acc;
      }, {});
      F.setTouched(touchedAll);
    }

    if (Object.keys(mainErrors).length > 0 || !destinationsValid) {
      if (Object.keys(mainErrors).length > 0) {
        focusFirstErrorField(mainErrors);
      } else {
        // ── main form ผ่านหมดแล้ว แต่จุดหมายที่ 2,3 มี error ── เปิด accordion ทุกจุดหมายให้เห็น ──
        setOpenCards((prev) => {
          const next = { ...prev };
          destinations.forEach((d) => { next[d._id] = true; });
          return next;
        });
      }
      return;
    }

    // ── ผ่านทั้งสองส่วนแล้ว ค่อยให้ Formik ดำเนินการ submit จริง (เรียก onSubมit ใน hook) ──
    F.handleSubmit(e);
  };

  const handleResubmitRequest = async (e) => {
    e.preventDefault();

    // ── validate destinations (จุดหมายที่ 2, 3) ──
    if (!validateDestinations()) return;

    const result = await showConfirm({
      title: "ยืนยันการส่งคำขออีกครั้ง",
      detail: "กด Confirm เพื่อดำเนินการต่อ",
    });
    if (!result.isConfirmed) return;
    try {
      showLoading();
      const payload = buildMainPayload();
      const res = await RequestService.SearchRequest({ id: F.values.id });
      const source = res.data.result?.find(r => r.id === F.values.id);
      const existingRecurring = source?.request_recurring ?? null;

      const finalPayload = { ...payload, id_workflow_status: 1, update_by: getUserEmail() };
      if (payload.request_recurring) {
        finalPayload.request_recurring = {
          ...payload.request_recurring,
          ...(existingRecurring && {
            id: existingRecurring.id,
            id_request: existingRecurring.id_request,
          }),
        };
      } else {
        finalPayload.request_recurring = null;
      }

      await RequestService.PatchRequest(F.values.id, finalPayload);
      hideLoading();
      await showSaveSuccess();
      navigate("/user/requests", { replace: true });
    } catch (err) {
      hideLoading();
      console.error("ResubmitRequest error:", err?.response?.data);
      showError();
    }
  };

  const [openCards, setOpenCards] = useState({ main: true });
  const toggleCard = (key) => setOpenCards((prev) => ({ ...prev, [key]: !prev[key] }));

  // ── flag บอกว่า destinations ที่เปลี่ยนแปลงล่าสุด มาจากการกดปุ่ม "เพิ่มจุดหมาย" เองหรือไม่ ──
  // ── ใช้แยกจากกรณีโหลดข้อมูลทีเดียวหลายตัว (loadRequestById ตอนเข้าดู detail, applyCloneDestinations ตอน clone) ──
  const manualAddRef = useRef(false);

  const handleAddDestination = () => {
    manualAddRef.current = true;
    addDestination();
  };

  useEffect(() => {
    // ── auto-open เฉพาะตอนกด "เพิ่มจุดหมาย" เองทีละตัวเท่านั้น ──
    // ── ถ้ามาจากการโหลดข้อมูลเดิม (view mode) หรือ clone หลายตัวพร้อมกัน จะไม่ auto-open เลย ──
    if (!manualAddRef.current) return;
    manualAddRef.current = false;

    if (destinations.length > 0) {
      const lastDest = destinations[destinations.length - 1];
      setOpenCards((prev) => ({ ...prev, [lastDest._id]: true }));
    }
  }, [destinations.length]);

  const [loadingClone, setLoadingClone] = useState(false);
  const debounceRef = useRef(null);
  const { loadingHolidays, isDateAvailable } = useHolidays();

  // ── disable วันที่ผ่านมาแล้ว + เสาร์-อาทิตย์ + วันหยุด backend สำหรับ Service Date ──
  const isServiceDateSelectable = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (date < today) return false; // ห้ามเลือกวันที่ผ่านไปแล้ว

    const day = date.getDay(); // 0 = อาทิตย์, 6 = เสาร์
    if (day === 0 || day === 6) return false;
    return isDateAvailable(date);
  };

  // ── Cutoff รอบส่ง (เฉพาะเมื่อ service_date = วันนี้) เทียบเป็นนาที ──
  // ── 00:01–09:00 เลือกรอบเช้าได้, 00:01–11:30 เลือกรอบบ่ายได้, 11:31 เป็นต้นไปเหลือ Urgent ──
  const MORNING_CUTOFF_MIN   = 9 * 60;        // 09:00
  const AFTERNOON_CUTOFF_MIN = 11 * 60 + 30;  // 11:30
  const getDeliverySectionOptions = (serviceDateStr, disableUrgent = false) => {
    const now = new Date();
    const isServiceDateToday = serviceDateStr === getTodayStr();
    const nowMin = now.getHours() * 60 + now.getMinutes();

    // ── cutoff ใช้เสมอเมื่อ service date = วันนี้ (รวมถึงตอนตั้ง recurring ด้วย) ──
    // ── ถ้าเลยเวลาตัดรอบแล้ว ต้องส่งวันถัดไป → เปลี่ยน service date เป็นวันหน้า ──
    const shouldDisableMorning   = isServiceDateToday && nowMin > MORNING_CUTOFF_MIN;
    const shouldDisableAfternoon = isServiceDateToday && nowMin > AFTERNOON_CUTOFF_MIN;

    return DELIVERY_SECTION_OPTIONS.map((opt) => ({
      ...opt,
      isDisabled:
        (opt.value === "1" && shouldDisableMorning) ||
        (opt.value === "2" && shouldDisableAfternoon) ||
        // ── มี recurring ตั้งอยู่แล้ว (rs-preview โชว์อยู่) ── ห้ามเลือก Urgent ──
        (opt.value === "3" && disableUrgent),
    }));
  };

  useEffect(() => {
    if (!isCloneData || !cloneRefNo.trim()) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoadingClone(true);
      try {
        const res = await RequestService.SearchRequest({ ref_no: cloneRefNo.trim() });
        const results = res.data.result;
        if (!results?.length) {
          Swal.fire({
            html: `
              <div class="swal2-html-container">
                <div class="swal-icon-wrapper"><i class="mdi mdi-alert-circle-outline"></i></div>
                <h2 class="swal-title">ไม่พบข้อมูลคำขอนี้</h2>
                <p class="swal-text">ตรวจสอบเลขที่คำขออีกครั้ง เพื่อดำเนินการต่อ</p>
              </div>
            `,
            showConfirmButton: true,
            confirmButtonText: "Ok",
            customClass: { confirmButton: "btn btn-primary", actions: "justify-content-center" },
            buttonsStyling: false,
          });
          return;
        }
        const source = results[0];

        if (source.request_recurring) {
          handleRecurringDelete();
        }

        const detail = source.request_details?.[0] ?? {};
        if (detail.destination_id_province) fetchDistricts(detail.destination_id_province);

        // ── reverse-map "อื่นๆ" กลับเป็น "other" (ใช้ helper ร่วมกับ user-request-form.js) ──
        // ── helper จับได้ทั้งข้อมูลเก่า (-1) และใหม่ (0) และดึงชื่อจาก field ที่ backend เก็บจริง ──
        const isCloneOtherPickup   = isOtherPickupDetail(detail);
        const isCloneOtherBuilding = isOtherBuildingDetail(detail);
        const isCloneOtherBranch   = isOtherProviderBranch(detail.id_service_type, detail.destination_id_provider_branch);

        setDataDetail({
          id: 0,
          service_date: "",
          pickup_location: isCloneOtherPickup ? "other" : (source.sender_id_bu ?? ""),
          other_pickup_name: isCloneOtherPickup ? (detail.origin_building_name ?? "") : "",
          document_type: source.document_type ?? "",
          delivery_section: source.delivery_section ?? "",
          is_complete_today: detail.complete_time_type != null ? Number(detail.complete_time_type) : null,
          // ── FIX: ใช้ extractTimeHHMM (import จาก user-request-form.js) แทน new Date().toTimeString() ──
          // ── เดิมเรียก extractTimeHHMM ในไฟล์นี้โดยไม่ได้ import/define มาก่อน ทำให้ error runtime ──
          time_complete_today: extractTimeHHMM(detail.complete_time_specific),
          document_secret: source.document_type ?? null,
          service_type: detail.id_service_type ?? "",
          delivery_type: detail.id_sub_service_type > 0 ? detail.id_sub_service_type : null,
          receiver_name: detail.destination_contact_name ?? "",
          receiver_tel: detail.destination_phone_number ?? "",
          receiver_company: detail.destination_company ?? "",
          receiver_department: detail.destination_department ?? "",
          receiver_floor: detail.destination_floor ?? "",
          receiver_building: isCloneOtherBuilding ? "other" : String(detail.destination_id_building || ""),
          other_building_name: isCloneOtherBuilding ? (detail.destination_name ?? "") : "",
          receiver_province: String(detail.destination_id_province || ""),
          receiver_district: String(detail.destination_id_district || ""),
          receiver_map: detail.destination_map ?? "",
          note: detail.note ?? "",
          id_provider: String(detail.destination_id_provider || ""),
          id_provider_branch: isCloneOtherBranch ? "other" : String(detail.destination_id_provider_branch || ""),
          other_branch_name: isCloneOtherBranch ? (detail.destination_name || detail.destination_contact_name || "") : "",
          id_sub_service_type3: detail.id_sub_service_type > 0 ? String(detail.id_sub_service_type) : "",
        });

        const providerId = String(detail.destination_id_provider || "");
        if (String(detail.id_service_type) === "2") {
          fetchProviders(); // ── โหลดรายชื่อธนาคารทั้งหมด ให้ SearchableSelect ของจุดหมายที่ 1 มี option match แสดงชื่อ ──
          if (providerId) fetchProviderBranches(providerId);
        }
        if (String(detail.id_service_type) === "3") {
          if (detail.id_sub_service_type > 0) {
            fetchTransactionProviders(Number(detail.id_sub_service_type));
          }
          if (providerId) {
            fetchTransactionBranches(providerId);
          }
        }

        // ── clone จุดหมายที่ 2, 3 (ถ้ามี) ──
        await applyCloneDestinations(source);
      } catch (err) {
        const msg = err?.response?.data?.message || err?.message || "unknown";
        Swal.fire({ icon: "warning", title: "ค้นหาไม่สำเร็จ", text: msg, confirmButtonText: "Ok", confirmButtonColor: "#3085d6" });
      } finally {
        setLoadingClone(false);
      }
    }, 600);
    return () => clearTimeout(debounceRef.current);
  }, [cloneRefNo, isCloneData]);

  const userInfoToShow = (dataUser?.id_user_group === 4 && senderInfo) ? senderInfo : dataUser;

  const hasRecurringPreview =
    (
      (recurringConfig?.freq_unit === "week" && recurringConfig?.repeat_days?.length > 0) ||
      (recurringConfig?.freq_unit === "month" && !!recurringConfig?.monthly_type)
    ) &&
    !!recurringConfig?.start_date &&
    !!recurringConfig?.end_date;

  const recurringPreviewText = hasRecurringPreview
    ? (recurringConfig.freq_unit === "week"
        ? `🕑 เกิดทุก${recurringConfig.repeat_days.map((i) => DAY_INDEX_TO_LABEL[i] ?? i).join("และ")} ตั้งแต่ ${formatDate(recurringConfig.start_date)} ถึง ${formatDate(recurringConfig.end_date)}`
        : `🕑 เกิดทุก ${recurringConfig.freq_num || 1} เดือน ตั้งแต่ ${formatDate(recurringConfig.start_date)} ถึง ${formatDate(recurringConfig.end_date)}`)
    : "";

  if (showRecurring) {
    return (
      <RecurringSetting
        initialValues={recurringConfig}
        onSave={handleReccurringSave}
        onSaveSuccess={() => navigate("/user/requests", { replace: true })}
        onBack={handleRecurringBack}
        serviceDate={F.values.service_date}
        onDelete={async () => {
          try {
            const res = await RequestService.SearchRequest({ id: F.values.id });
            const source = res.data.result?.find(r => r.id === F.values.id);
            const recurringId = source?.request_recurring?.id;
            if (recurringId) {
              await RequestService.DeleteRequestRecurring(recurringId);
            }
            // ── ลบสำเร็จจริง (หรือไม่มี recurring ให้ลบอยู่แล้ว) ค่อยเคลียร์ state ฝั่ง FE ──
            handleRecurringDelete();
            F.setFieldValue("delivery_section", "");
          } catch (err) {
            console.error('deleteRecurring error:', err);
            // ── ลบไม่สำเร็จ ห้ามเคลียร์ recurringConfig ฝั่ง FE เด็ดขาด ──
            // ── ไม่งั้น user จะเห็นเหมือนลบสำเร็จ ทั้งที่ DB ยังมี recurring ผูกอยู่ ──
            // ── แล้วไป Resubmit ทีหลังจะเจอ error "request_recurring is required" แบบเงียบๆ ──
            showError();
          }
        }}
        buildMainPayload={buildMainPayload}
      />
    );
  }

  if (showRecurringPreview) {
    return (
      <RecurringPreview
        dates={recurringPreviewDates}
        buildMainPayload={buildMainPayload}
        onBack={() => setShowRecurringPreview(false)}
        onCancel={() => setShowRecurringPreview(false)}
        onSaveSuccess={() => navigate("/user/requests", { replace: true })}
      />
    );
  }

  return (
    <React.Fragment>
      <div className="page-wrapper">
        <div className="page-title">
          {F.values.id > 0 ? "รายละเอียดคำขอ" : "สร้างใบคำขอ Create request"}
          {F.values.id > 0 && refNo && (
            <div className="page-subtitle">
              Ref No. {refNo}
            </div>
          )}
        </div>

        {F.values.id_workflow_status === WF.RETURN && (
          <>
            <span className="detail-status-badge detail-status-badge--return">
              <i className="ri-feedback-fill" /> Returned by admin
            </span>
            {returnNote && (
              <div className="admin-remark-box">
                <div className="admin-remark-box__label">Admin ส่งกลับเพื่อแก้ไข</div>
                <div className="admin-remark-box__text">{returnNote}</div>
              </div>
            )}
          </>
        )}

        {F.values.id_workflow_status === WF.ADMIN_CANCEL && (
          <>
            <span className="detail-status-badge detail-status-badge--cancel">
              <i className="ri-feedback-fill" /> Cancel
            </span>
            {returnNote && (
              <div className="admin-remark-box admin-remark-box--cancel">
                <div className="admin-remark-box__label">เหตุผลการยกเลิก</div>
                <div className="admin-remark-box__text">{returnNote}</div>
              </div>
            )}
          </>
        )}

        {[WF.ASSIGN, WF.DOING, WF.INCOMPLETE, WF.REASSIGN].includes(F.values.id_workflow_status) && (
          <span className="detail-status-badge detail-status-badge--inprogress">
            <i className="ri-feedback-fill" /> In-progress
          </span>
        )}

        {[WF.COMPLETE, WF.CLOSE_OK, WF.CLOSE_COND].includes(F.values.id_workflow_status) && (
          <span className="detail-status-badge detail-status-badge--delivered">
            <i className="ri-feedback-fill" /> Complete
          </span>
        )}

        <form onSubmit={handleCreateSubmit}>
          <UserInfoSection dataUser={userInfoToShow} />

          <div className="section-label">
            Request detail (ข้อมูลคำขอ) 1/{MAX_EXTRA_DESTINATIONS + 1}
          </div>

          {!isViewMode && (
            <CloneSection
              isCloneData={isCloneData} setIsCloneData={setIsCloneData}
              cloneRefNo={cloneRefNo} setCloneRefNo={setCloneRefNo}
              loadingClone={loadingClone}
            />
          )}

          {/* ── Section แยกออกมาข้างนอก accordion ── */}
          <div className="card mb-3">
            <div className="form-row">
              <div className="form-group">
                <label className="form-label" htmlFor="service_date">
                  วันที่ให้บริการ (Service Date) <span className="req">*</span>
                </label>
                <FormDatePicker
                  value={F.values.service_date ? new Date(F.values.service_date) : null}
                  onChange={(date) => {
                    if (!date) { F.setFieldValue("service_date", ""); return; }
                    const year = date.getFullYear();
                    const month = String(date.getMonth() + 1).padStart(2, "0");
                    const day = String(date.getDate()).padStart(2, "0");
                    F.setFieldValue("service_date", `${year}-${month}-${day}`);
                  }}
                  isInvalid={F.touched.service_date && !!F.errors.service_date}
                  disabled={formIsDisabled || loadingHolidays}
                  filterDate={isServiceDateSelectable}
                  minDate={new Date()}
                  placeholderText={loadingHolidays ? "กำลังโหลดวันหยุด..." : undefined}
                />
                {showErr("service_date")}
              </div>
              <div className="form-group mb">
                <label className="form-label" htmlFor="delivery_section">
                  รอบการส่งมอบเอกสาร (Delivery Session) <span className="req">*</span>
                </label>
                <SearchableSelect
                  id="delivery_section"
                  name="delivery_section"
                  value={F.values.delivery_section}
                  onChange={(e) => {
                    const val = e.target.value ? Number(e.target.value) : "";
                    F.setFieldValue("delivery_section", val);
                    if (val === 3) handleRecurringDelete(false);
                  }}
                  onBlur={fProps("delivery_section").onBlur}
                  disabled={formIsDisabled}
                  isInvalid={fProps("delivery_section").className.includes("is-invalid")}
                  options={getDeliverySectionOptions(F.values.service_date, hasRecurringPreview)}
                  placeholder="เลือกรอบ"
                />
                {showErr("delivery_section")}
              </div>
            </div>

            <div className="form-group mb">
              <label className="form-label">
                ประเภทของเอกสาร (Request Type) <span className="req">*</span>
              </label>
              <RadioGroup
                name="document_secret"
                options={DOCUMENT_SECRET_OPTIONS}
                value={F.values.document_secret}
                onChange={(val) => F.setFieldValue("document_secret", val)}
                disabled={formIsDisabled}
                touched={F.touched.document_secret}
                error={F.errors.document_secret}
              />
            </div>

            {!formIsDisabled && (
                <>
                  {hasRecurringPreview && (
                    <div className="rs-preview mb-3">{recurringPreviewText}</div>
                  )}
                  <button
                    type="button"
                    className="btn-action-outline"
                    onClick={() => setShowRecurring(true)}
                    disabled={
                      formIsDisabled ||
                      Number(F.values.delivery_section) === 3 ||
                      !F.values.service_date
                    }
                  >
                    <i className="ri-repeat-2-fill" /> ตั้งค่าให้ทำซ้ำ
                  </button>
                </>
              )}
          </div>

          {/* ── Accordion จุดหมายที่ 1 ── */}
          <AccordionCard
            title="จุดหมายที่ 1"
            refNo={
              F.values.id > 0 && refNo
                ? (destinations.length > 0 ? `${refNo}-1` : refNo)
                : undefined
            }
            isOpen={!!openCards.main}
            onToggle={() => toggleCard("main")}
            showRemove={false}
          >
            <div className="form-row">
              <div className="form-group">
                <label className="form-label" htmlFor="pickup_location">
                  จุดเริ่มต้น (Document Pickup Location) <span className="req">*</span>
                </label>
                <SearchableSelect
                    id="pickup_location"
                    name="pickup_location"
                    value={F.values.pickup_location}
                    onChange={fProps("pickup_location").onChange}
                    onBlur={fProps("pickup_location").onBlur}
                    disabled={formIsDisabled}
                    isInvalid={fProps("pickup_location").className.includes("is-invalid")}
                    options={[
                      ...buildings.map((b) => ({ value: String(b.id), label: b.name_th })),
                      { value: OTHER_PICKUP_VALUE, label: "อื่นๆ" },
                    ]}
                    placeholder="เลือกสถานที่"
                  />
                  {showErr("pickup_location")}
                {F.values.pickup_location === OTHER_PICKUP_VALUE && (
                  <div style={{ marginTop: 8 }}>
                    <input
                      {...fProps("other_pickup_name")}
                      placeholder="ระบุสถานที่"
                      autoComplete="off"
                    />
                  </div>
                )}
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="service_type">
                  ประเภทบริการ (Service Type) <span className="req">*</span>
                </label>
                <SearchableSelect
                  id="service_type"
                  name="service_type"
                  value={F.values.service_type}
                  onChange={handleServiceTypeChange}
                  onBlur={fProps("service_type").onBlur}
                  disabled={formIsDisabled}
                  isInvalid={fProps("service_type").className.includes("is-invalid")}
                  options={serviceTypes.map((item) => ({ value: String(item.id), label: item.name }))}
                  placeholder="เลือกบริการ"
                />
                {showErr("service_type")}
              </div>
            </div>

            {isDocumentDelivery(F.values.service_type) && (
              <div className="form-group mb">
                <label className="form-label">
                  เลือกแบบการรับส่งเอกสาร <span className="req">*</span>
                </label>
                <RadioGroup
                  name="delivery_type"
                  options={DELIVERY_TYPE_OPTIONS}
                  value={F.values.delivery_type}
                  onChange={(val) => F.setFieldValue("delivery_type", val)}
                  disabled={formIsDisabled}
                  touched={F.touched.delivery_type}
                  error={F.errors.delivery_type}
                />
              </div>
            )}

            {String(F.values.service_type) === "3" && (
              <div className="form-group mb">
                <label className="form-label">
                  ชำระบริการด้านใด <span className="req">*</span>
                </label>
                {/* ── ดึงจาก DB (m_sub_service_type) ผ่าน subServiceTypes ที่ hook fetch มาแทน hardcode ── */}
                <div className="radio-group">
                  {loadingSubServiceTypes ? (
                    <span>กำลังโหลด...</span>
                  ) : (
                    paymentTypeOptions.map((opt) => (
                      <label className="radio-option" key={opt.value}>
                        <input
                          type="radio"
                          name="id_sub_service_type3"
                          value={opt.value}
                          checked={String(F.values.id_sub_service_type3) === opt.value}
                          onChange={() => {
                            F.setFieldValue("id_sub_service_type3", opt.value);
                            F.setFieldValue("id_provider", "");
                            F.setFieldValue("id_provider_branch", "");
                            fetchTransactionProviders(Number(opt.value));
                          }}
                          disabled={formIsDisabled}
                        />
                        {opt.label}
                      </label>
                    ))
                  )}
                </div>
                {showErr("id_sub_service_type3")}
              </div>
            )}

            <div className="form-group mb">
              <label className="form-label">
                เวลาที่ต้องดำเนินการให้เสร็จ (Completion Time) <span className="req">*</span>
              </label>
              <RadioGroup
                name="is_complete_today"
                options={COMPLETION_TIME_OPTIONS}
                value={F.values.is_complete_today}
                onChange={(val) => F.setFieldValue("is_complete_today", val)}
                disabled={formIsDisabled}
                touched={F.touched.is_complete_today}
                error={F.errors.is_complete_today}
              />
            </div>

            {Number(F.values.is_complete_today) === 2 && (
              <div className="form-group mb">
                <label className="form-label" htmlFor="time_complete_today">
                  เวลา (Time) <span className="req">*</span>
                </label>
                <input
                  type="time"
                  {...fProps("time_complete_today")}
                  min={getMinCompletionTime(F.values.service_date)}
                  onChange={(e) => {
                    const val = e.target.value;
                    // ── ถ้าเลือกเวลาที่ผ่านมาแล้ว (service_date = วันนี้) ไม่ยอมรับค่านั้น ──
                    if (!isCompletionTimeNotPast(F.values.service_date, val)) return;
                    F.setFieldValue("time_complete_today", val);
                  }}
                  style={{ maxWidth: 200 }}
                />
                {showErr("time_complete_today")}
              </div>
            )}

            {isDocumentDelivery(F.values.service_type) && (
              <DocumentDeliverySection
                fProps={fProps} showErr={showErr} formIsDisabled={formIsDisabled}
                contactFieldsDisabled={lockContactInfo}
                buildings={buildings} provinces={provinces} districts={districts}
                loadingDistricts={loadingDistricts} onProvinceChange={handleProvinceChange}
                onBuildingChange={(building) => {
                  // ── clear ค่าเดิม (จังหวัด, เขต, แผนที่, ชั้น) ก่อน แล้วค่อย auto-fill จาก building ใหม่ ──
                  // ── ป้องกันค่าเก่าค้าง กรณี building ใหม่ไม่มีข้อมูลบาง field ──
                  F.setFieldValue("receiver_province", "");
                  F.setFieldValue("receiver_district", "");
                  F.setFieldValue("receiver_map", "");
                  F.setFieldValue("receiver_floor", "");

                  // auto-fill province, district, map จาก building ที่เลือก
                  // รอ backend return id_province, id_district ใน buildings API
                  if (building.id_province) {
                    F.setFieldValue("receiver_province", String(building.id_province));
                    handleProvinceChange({ target: { value: String(building.id_province) } });
                  }
                  if (building.id_district) {
                    F.setFieldValue("receiver_district", String(building.id_district));
                  }
                  if (building.map) {
                    F.setFieldValue("receiver_map", building.map);
                  }
                }}
              />
            )}
            {String(F.values.service_type) === "2" && (
              <BankingSection
                fProps={fProps} showErr={showErr} formIsDisabled={formIsDisabled}
                providers={providers} loadingProviders={loadingProviders}
                providerBranches={providerBranches} loadingBranches={loadingBranches}
                onProviderChange={(e) => {
                  const val = e.target.value;
                  F.setFieldValue("id_provider", val);
                  F.setFieldValue("id_provider_branch", "");
                  F.setFieldValue("provider_branch_name", "");
                  F.setFieldValue("other_branch_name", "");
                  if (val) fetchProviderBranches(val);
                }}
                onBranchChange={(e) => {
                  const val = e.target.value;
                  F.setFieldValue("id_provider_branch", val);
                  F.setFieldValue("other_branch_name", ""); // ── clear ชื่อสาขาที่กรอกเอง ทุกครั้งที่เปลี่ยนสาขา ──
                  const found = providerBranches.find(b => String(b.id) === String(val));
                  F.setFieldValue("provider_branch_name", found?.name_th || "");
                  // ── auto-fill Google Map จากสาขาที่เลือก, เคลียร์ถ้าเลือก "อื่นๆ" ──
                  F.setFieldValue("receiver_map", val === "other" ? "" : (found?.map || ""));
                }}
              />
            )}
            {String(F.values.service_type) === "3" && (
              <TransactionSection
                fProps={fProps}
                showErr={showErr}
                formIsDisabled={formIsDisabled}
                transactionProviders={transactionProviders}
                loadingTransactionProviders={loadingTransactionProviders}
                transactionBranches={transactionBranches}
                loadingTransactionBranches={loadingTransactionBranches}
                onProviderChange={(e) => {
                  const val = e.target.value;
                  F.setFieldValue("id_provider", val);
                  F.setFieldValue("id_provider_branch", "");
                  F.setFieldValue("other_branch_name", ""); // ── clear ชื่อสาขาที่กรอกเอง เมื่อเปลี่ยน provider ──
                  if (val) fetchTransactionBranches(val);
                }}
                onBranchChange={(e) => {
                  const val = e.target.value;
                  F.setFieldValue("id_provider_branch", val);
                  F.setFieldValue("other_branch_name", ""); // ── clear ชื่อสาขาที่กรอกเอง ทุกครั้งที่เปลี่ยนสาขา ──
                  // ── auto-fill Google Map จากสาขาที่เลือก, เคลียร์ถ้าเลือก "อื่นๆ" ──
                  const found = transactionBranches.find(b => String(b.id) === String(val));
                  F.setFieldValue("receiver_map", val === "other" ? "" : (found?.map || ""));
                }}
              />
            )}

            {formIsDisabled && <MessengerSection messenger={F.values.messenger} />}
            {formIsDisabled && <AdminSection admin={adminInfo} />}

            <p className="note-warning">
              *คำขอที่อยู่ในสถานะ 'In-progress' แล้ว จะไม่สามารถแก้ไขหรือยกเลิกคำขอได้ 
              กรุณาติดต่อฝ่ายดูแล โทร 02-080-1000 ต่อ 1028, 1029, 1032 , 1036 หากมีข้อสงสัย
            </p>
          </AccordionCard>

          {destinations.map((dest, index) => {
            const destFProps = (field) => ({
              id: `dest_${dest._id}_${field}`,
              name: field,
              value: dest[field] ?? "",
              onChange: (e) => updateDestination(dest._id, field, e.target.value),
              onBlur: () => {},
              disabled: formIsDisabled,
              className:
                "form-control" +
                (showDestErr(dest._id, field) ? " is-invalid" : ""),
            });

            const destSetField = (field, val) => updateDestination(dest._id, field, val);
            const destShowErr = (field) => showDestErr(dest._id, field);

            return (
              <React.Fragment key={dest._id}>
                <div className="section-label">
                  Request detail (ข้อมูลคำขอ) {index + 2}/{MAX_EXTRA_DESTINATIONS + 1}
                </div>

                <AccordionCard
                  title={`จุดหมายที่ ${index + 2}`}
                  refNo={refNo && dest.seq ? `${refNo}-${dest.seq}` : undefined}
                  isOpen={!!openCards[dest._id]}
                  onToggle={() => toggleCard(dest._id)}
                  showRemove={!formIsDisabled}
                  onRemove={() => removeDestination(dest._id)}
                  disabled={formIsDisabled}
                >
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">
                        จุดเริ่มต้น (Document Pickup Location) <span className="req">*</span>
                      </label>
                      <SearchableSelect
                        id={`dest_${dest._id}_pickup_location`}
                        name="pickup_location"
                        value={dest.pickup_location}
                        onChange={destFProps("pickup_location").onChange}
                        onBlur={() => {}}
                        disabled={formIsDisabled}
                        isInvalid={!!destShowErr("pickup_location")}
                        options={[
                          ...buildings.map((b) => ({ value: String(b.id), label: b.name_th })),
                          { value: OTHER_PICKUP_VALUE, label: "อื่นๆ" },
                        ]}
                        placeholder="เลือกสถานที่"
                      />
                      {destShowErr("pickup_location")}
                      {dest.pickup_location === OTHER_PICKUP_VALUE && (
                        <div style={{ marginTop: 8 }}>
                          <input
                            {...destFProps("other_pickup_name")}
                            placeholder="ระบุสถานที่"
                            autoComplete="off"
                          />
                        </div>
                      )}
                    </div>
                    <div className="form-group">
                      <label className="form-label">
                        ประเภทบริการ (Service Type) <span className="req">*</span>
                      </label>
                      <SearchableSelect
                        id={`dest_${dest._id}_service_type`}
                        name="service_type"
                        value={dest.service_type}
                        onChange={(e) => {
                          const val = e.target.value;
                          destSetField("service_type", val);
                          destSetField("delivery_type", null);
                          destSetField("id_provider", "");
                          destSetField("id_provider_branch", "");
                          destSetField("id_sub_service_type3", "");
                          if (String(val) === "2") {
                            fetchDestProviders();
                          } else if (String(val) === "3") {
                            setDestProviders([]);
                            setDestProviderBranches([]);
                            if (!subServiceTypes?.length) fetchSubServiceTypes();
                          }
                        }}
                        onBlur={() => {}}
                        disabled={formIsDisabled}
                        isInvalid={!!destShowErr("service_type")}
                        options={serviceTypes.map((item) => ({ value: String(item.id), label: item.name }))}
                        placeholder="เลือกบริการ"
                      />
                      {destShowErr("service_type")}
                    </div>
                  </div>

                  {isDocumentDelivery(dest.service_type) && (
                    <div className="form-group mb">
                      <label className="form-label">
                        เลือกแบบการรับส่งเอกสาร <span className="req">*</span>
                      </label>
                      <RadioGroup
                        name={`dest_${dest._id}_delivery_type`}
                        options={DELIVERY_TYPE_OPTIONS}
                        value={dest.delivery_type}
                        onChange={(val) => destSetField("delivery_type", val)}
                        disabled={formIsDisabled}
                      />
                      {destShowErr("delivery_type")}
                    </div>
                  )}

                  {String(dest.service_type) === "3" && (
                    <div className="form-group mb">
                      <label className="form-label">
                        ชำระบริการด้านใด <span className="req">*</span>
                      </label>
                      {/* ── ดึงจาก DB (m_sub_service_type) ผ่าน subServiceTypes ที่ hook fetch มาแทน hardcode ── */}
                      <div className="radio-group">
                        {loadingSubServiceTypes ? (
                          <span>กำลังโหลด...</span>
                        ) : (
                          paymentTypeOptions.map((opt) => (
                            <label className="radio-option" key={opt.value}>
                              <input
                                type="radio"
                                name={`dest_${dest._id}_id_sub_service_type3`}
                                value={opt.value}
                                checked={String(dest.id_sub_service_type3) === opt.value}
                                onChange={() => {
                                  destSetField("id_sub_service_type3", opt.value);
                                  destSetField("id_provider", "");
                                  destSetField("id_provider_branch", "");
                                  fetchTransactionProviders(Number(opt.value));
                                }}
                                disabled={formIsDisabled}
                              />
                              {opt.label}
                            </label>
                          ))
                        )}
                      </div>
                      {destShowErr("id_sub_service_type3")}
                    </div>
                  )}

                  <div className="form-group mb">
                    <label className="form-label">
                      เวลาที่ต้องดำเนินการให้เสร็จ (Completion Time) <span className="req">*</span>
                    </label>
                    <RadioGroup
                      name={`dest_${dest._id}_is_complete_today`}
                      options={COMPLETION_TIME_OPTIONS}
                      value={dest.is_complete_today}
                      onChange={(val) => destSetField("is_complete_today", val)}
                      disabled={formIsDisabled}
                    />
                    {destShowErr("is_complete_today")}
                  </div>

                  {Number(dest.is_complete_today) === 2 && (
                    <div className="form-group mb">
                      <label className="form-label">เวลา (Time) <span className="req">*</span></label>
                      <input
                        type="time"
                        {...destFProps("time_complete_today")}
                        min={getMinCompletionTime(F.values.service_date)}
                        onChange={(e) => {
                          const val = e.target.value;
                          // ── ถ้าเลือกเวลาที่ผ่านมาแล้ว (service_date = วันนี้) ไม่ยอมรับค่านั้น ──
                          if (!isCompletionTimeNotPast(F.values.service_date, val)) return;
                          destSetField("time_complete_today", val);
                        }}
                        style={{ maxWidth: 200 }}
                      />
                      {destShowErr("time_complete_today")}
                    </div>
                  )}

                  {isDocumentDelivery(dest.service_type) && (
                    <DocumentDeliverySection
                      fProps={destFProps} showErr={destShowErr} formIsDisabled={formIsDisabled}
                      contactFieldsDisabled={lockContactInfo}
                      buildings={buildings} provinces={provinces}
                      districts={destDistricts[dest._id] || []}
                      loadingDistricts={!!loadingDestDistricts[dest._id]}
                      onProvinceChange={(e) => {
                        const val = e.target.value;
                        destSetField("receiver_province", val);
                        destSetField("receiver_district", ""); // ── clear เขตเดิมทุกครั้งที่เปลี่ยนจังหวัดเอง ──
                        if (val) fetchDestDistricts(dest._id, val);
                      }}
                      onBuildingChange={(building) => {
                        // ── clear ค่าเดิม (จังหวัด, เขต, แผนที่, ชั้น) ก่อน แล้วค่อย auto-fill จาก building ใหม่ ──
                        destSetField("receiver_province", "");
                        destSetField("receiver_district", "");
                        destSetField("receiver_map", "");
                        destSetField("receiver_floor", "");

                        if (building.id_province) {
                          destSetField("receiver_province", String(building.id_province));
                          fetchDestDistricts(dest._id, building.id_province);
                        }
                        if (building.id_district) {
                          destSetField("receiver_district", String(building.id_district));
                        }
                        if (building.map) {
                          destSetField("receiver_map", building.map);
                        }
                      }}
                    />
                  )}

                  {String(dest.service_type) === "2" && (
                    <BankingSection
                      fProps={destFProps} showErr={destShowErr} formIsDisabled={formIsDisabled}
                      providers={destProviders}
                      loadingProviders={loadingDestProviders}
                      providerBranches={destProviderBranches}
                      loadingBranches={loadingDestBranches}
                      onProviderChange={(e) => {
                        destSetField("id_provider", e.target.value);
                        destSetField("id_provider_branch", "");
                        destSetField("provider_branch_name", "");
                        destSetField("other_branch_name", "");
                        if (e.target.value) fetchDestProviderBranches(e.target.value);
                      }}
                      onBranchChange={(e) => {
                        const val = e.target.value;
                        destSetField("id_provider_branch", val);
                        destSetField("other_branch_name", ""); // ── clear ชื่อสาขาที่กรอกเอง ทุกครั้งที่เปลี่ยนสาขา ──
                        const found = destProviderBranches.find(b => String(b.id) === String(val));
                        destSetField("provider_branch_name", found?.name_th || "");
                        // ── auto-fill Google Map จากสาขาที่เลือก, เคลียร์ถ้าเลือก "อื่นๆ" ──
                        destSetField("receiver_map", val === "other" ? "" : (found?.map || ""));
                      }}
                    />
                  )}

                  {String(dest.service_type) === "3" && (
                    <TransactionSection
                      fProps={destFProps}
                      showErr={destShowErr}
                      formIsDisabled={formIsDisabled}
                      transactionProviders={transactionProviders}
                      loadingTransactionProviders={loadingTransactionProviders}
                      transactionBranches={transactionBranches}
                      loadingTransactionBranches={loadingTransactionBranches}
                      onProviderChange={(e) => {
                        const val = e.target.value;
                        destSetField("id_provider", val);
                        destSetField("id_provider_branch", "");
                        destSetField("other_branch_name", ""); // ── clear ชื่อสาขาที่กรอกเอง เมื่อเปลี่ยน provider ──
                        if (val) fetchTransactionBranches(val);
                      }}
                      onBranchChange={(e) => {
                        const val = e.target.value;
                        destSetField("id_provider_branch", val);
                        destSetField("other_branch_name", ""); // ── clear ชื่อสาขาที่กรอกเอง ทุกครั้งที่เปลี่ยนสาขา ──
                        // ── auto-fill Google Map จากสาขาที่เลือก, เคลียร์ถ้าเลือก "อื่นๆ" ──
                        const found = transactionBranches.find(b => String(b.id) === String(val));
                        destSetField("receiver_map", val === "other" ? "" : (found?.map || ""));
                      }}
                    />
                  )}

                  <p className="note-warning">
                    *คำขอที่อยู่ในสถานะ 'In-progress' แล้ว จะไม่สามารถแก้ไขหรือยกเลิกคำขอได้
                    กรุณาติดต่อฝ่ายดูแล โทร 02-080-1000 ต่อ 1028, 1028, 1029, 1032 หากมีข้อสงสัย
                  </p>

                  {formIsDisabled && <MessengerSection messenger={F.values.messenger} />}
                  {formIsDisabled && <AdminSection admin={adminInfo} />}
                </AccordionCard>
              </React.Fragment>
            );
          })}

          {!formIsDisabled && F.values.service_type && destinations.length < MAX_EXTRA_DESTINATIONS && (
            <button type="button" className="btn-action" onClick={handleAddDestination}>
              <i className="ri-add-circle-line" /> เพิ่มจุดหมาย
            </button>
          )}

          <div className="form-footer">
            <div className="footer-actions">
              {isViewMode && !formIsDisabled && (
                <button type="button" className="btn btn-cancel" onClick={handleCancelRequest}>
                  Cancel
                </button>
              )}
              <div className="footer-right">
                <button
                  type="button"
                  className="btn btn-secondary btn-back"
                  onClick={() => navigate("/user/requests")}
                >
                  <i className="ri-arrow-left-line" /> Back
                </button>
                {isViewMode && formIsDisabled && (
                  <button type="button" className="btn btn-primary"
                    onClick={handlePrint}>
                    Print Form
                  </button>
                )}
                {isViewMode && !formIsDisabled && (
                  <button type="button" className="btn btn-primary-print"
                    onClick={handlePrint}>
                    Print
                  </button>
                )}
                {!isViewMode && (
                  <button type="submit" className="btn btn-primary">Submit</button>
                )}
                {isViewMode && !formIsDisabled && F.values.id_workflow_status !== 3 && (
                  <button type="button" className="btn btn-primary" onClick={handleSaveRequest}>
                    Save
                  </button>
                )}
                {isViewMode && !formIsDisabled && F.values.id_workflow_status === 3 && (
                  <button type="button" className="btn btn-primary" onClick={handleResubmitRequest}>
                    Resubmit
                  </button>
                )}
              </div>
            </div>
          </div>
        </form>
      </div>
    </React.Fragment>
  );
};

export default UserRequestDetail;